/**
 * VipOnboardingCelebration — Modal festif affiché après une signature réussie.
 *
 * Éléments :
 *  - Canvas confetti or/vert Kempferhof (particules physiques)
 *  - Crest Kempferhof qui scale-in avec bloom doré
 *  - Titre bienvenue en Playfair Display
 *  - Message du directeur (avec photo si dispo)
 *  - Kit membre : jusqu'à 4 documents téléchargeables
 *  - Bouton "Continuer" retour à l'app
 *
 * Aucune dépendance externe — animation CSS + Canvas natif.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Download, FileText, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

/** Confetti canvas — particules or et vert Kempferhof. */
function ConfettiCanvas({ active }) {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const rafRef = useRef(null);
  const activeRef = useRef(active);

  useEffect(() => { activeRef.current = active; }, [active]);

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const setSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    setSize();
    window.addEventListener('resize', setSize);

    const colors = ['#d4a850', '#f0d78a', '#b78a1e', '#1e3a2c', '#2a5140', '#ffffff', '#f5efe0'];
    const shapes = ['square', 'circle', 'ribbon'];

    // Spawn en 3 vagues pour effet feu d'artifice
    const spawnBatch = (count) => {
      for (let i = 0; i < count; i++) {
        const x = canvas.width / 2 + (Math.random() - 0.5) * 200;
        const y = canvas.height / 2 + (Math.random() - 0.5) * 100;
        const angle = Math.random() * Math.PI * 2;
        const speed = 6 + Math.random() * 9;
        particlesRef.current.push({
          x, y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 3, // impulsion vers le haut
          size: 5 + Math.random() * 6,
          color: colors[Math.floor(Math.random() * colors.length)],
          shape: shapes[Math.floor(Math.random() * shapes.length)],
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.3,
          opacity: 1,
          gravity: 0.14 + Math.random() * 0.08,
          drag: 0.985,
          life: 0,
        });
      }
    };
    spawnBatch(90);
    const wave2 = setTimeout(() => spawnBatch(70), 400);
    const wave3 = setTimeout(() => spawnBatch(50), 900);

    const loop = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesRef.current = particlesRef.current.filter((p) => p.opacity > 0.02);
      for (const p of particlesRef.current) {
        p.vy += p.gravity;
        p.vx *= p.drag;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.rotationSpeed;
        p.life += 1;
        if (p.life > 60) p.opacity -= 0.008;
        if (p.y > canvas.height + 20) p.opacity = 0;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.globalAlpha = Math.max(0, p.opacity);
        ctx.fillStyle = p.color;
        if (p.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        } else if (p.shape === 'ribbon') {
          ctx.fillRect(-p.size / 2, -p.size / 8, p.size, p.size / 4);
        } else {
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
        }
        ctx.restore();
      }
      if (activeRef.current || particlesRef.current.length > 0) {
        rafRef.current = requestAnimationFrame(loop);
      }
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      window.removeEventListener('resize', setSize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      clearTimeout(wave2);
      clearTimeout(wave3);
      particlesRef.current = [];
    };
  }, [active]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-[200]"
      data-testid="vip-confetti-canvas"
    />
  );
}

export default function VipOnboardingCelebration({ contract, onClose }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem('client_portal_token');
      const r = await fetch(`${API}/api/client-portal/onboarding`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        setConfig(await r.json());
      } else {
        setConfig({ director_name: 'La Direction', director_title: 'Golf du Kempferhof', welcome_message: '', kit_documents: [] });
      }
    } catch {
      setConfig({ director_name: 'La Direction', director_title: 'Golf du Kempferhof', welcome_message: '', kit_documents: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDownloadKit = (doc) => {
    const token = localStorage.getItem('client_portal_token');
    window.open(`${API}${doc.download_url}?token=${encodeURIComponent(token)}`, '_blank');
  };

  return (
    <>
      <ConfettiCanvas active={true} />

      <div
        className="fixed inset-0 z-[199] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-hidden"
        data-testid="vip-celebration-overlay"
      >
        {/* Fond décoratif — halo doré animé */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full opacity-30 bg-gradient-radial from-[#d4a850] via-[#d4a850]/20 to-transparent blur-3xl vip-pulse" />
        </div>

        <div
          className="relative bg-[#fdfcf8] rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-2xl h-[100dvh] sm:h-auto sm:max-h-[92dvh] overflow-hidden vip-fade-in border-2 border-[#d4a850]/60 flex flex-col"
          data-testid="vip-celebration-modal"
        >
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-10 text-[#8a7a56] hover:text-[#0d1a12] bg-white/80 rounded-full p-1.5 shadow-sm"
            data-testid="vip-close-btn"
          >
            <X size={18} />
          </button>

          {/* Header avec crest — gradient sombre + or */}
          <div className="relative bg-gradient-to-br from-[#0d1a12] via-[#1e3a2c] to-[#0d1a12] px-6 sm:px-8 py-6 sm:py-10 text-center overflow-hidden flex-shrink-0">
            {/* Motif géo doré */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-24 sm:w-32 h-24 sm:h-32 border-t-2 border-l-2 border-[#d4a850]" />
              <div className="absolute bottom-0 right-0 w-24 sm:w-32 h-24 sm:h-32 border-b-2 border-r-2 border-[#d4a850]" />
            </div>

            <div className="relative z-10">
              {/* Crest / initiales stylisées */}
              <div className="vip-crest inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#d4a850]/10 border-2 border-[#d4a850] mb-3 sm:mb-4">
                <Sparkles size={28} className="text-[#d4a850]" />
              </div>

              <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] sm:tracking-[0.4em] text-[#d4a850]/80 mb-2">
                Bienvenue au Kempferhof
              </div>

              <h1
                className="text-2xl sm:text-3xl md:text-4xl text-[#f5efe0] mb-2 leading-tight"
                style={{ fontFamily: 'Playfair Display, Cormorant Garamond, serif', fontWeight: 400 }}
              >
                Votre signature est enregistrée
              </h1>

              <p className="text-xs sm:text-sm text-[#d4a850]/90 italic max-w-md mx-auto">
                {contract?.template_label ? (
                  <>Contrat « {contract.template_label} » officialisé.</>
                ) : (
                  <>Votre engagement est officiel.</>
                )}
              </p>
            </div>
          </div>

          {/* Body */}
          <div className="p-4 sm:p-6 md:p-8 overflow-y-auto flex-1 min-h-0">
            {loading ? (
              <div className="text-center text-neutral-400 italic py-6">Un instant…</div>
            ) : (
              <>
                {/* Message du directeur */}
                <div className="mb-5 sm:mb-6">
                  <div className="flex items-start gap-3 sm:gap-4 mb-3">
                    {config?.director_photo_url ? (
                      <img
                        src={config.director_photo_url}
                        alt={config.director_name}
                        className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-[#d4a850]/50 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-[#d4a850]/30 to-[#b78a1e]/20 border-2 border-[#d4a850]/50 flex items-center justify-center flex-shrink-0">
                        <span className="text-[#8a6d1e] text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
                          {(config?.director_name || 'K')[0]}
                        </span>
                      </div>
                    )}
                    <div>
                      <div className="text-sm font-semibold text-[#0d1a12]">{config?.director_name || 'La Direction'}</div>
                      <div className="text-xs text-[#8a7a56] italic">{config?.director_title || 'Golf du Kempferhof'}</div>
                    </div>
                  </div>
                  <div
                    className="relative pl-3 sm:pl-4 border-l-2 border-[#d4a850]/60 text-neutral-700 whitespace-pre-line leading-relaxed text-sm sm:text-[15px]"
                    style={{ fontFamily: 'Georgia, "Cormorant Garamond", serif' }}
                    data-testid="vip-director-message"
                  >
                    {config?.welcome_message}
                  </div>
                </div>

                {/* Kit membre */}
                {config?.kit_documents?.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-wider text-[#8a7a56] font-medium mb-3 flex items-center gap-1.5">
                      <div className="h-px flex-1 bg-[#d4a850]/40" />
                      <span>Votre kit membre</span>
                      <div className="h-px flex-1 bg-[#d4a850]/40" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="vip-kit-documents">
                      {config.kit_documents.map((doc) => (
                        <button
                          key={doc.id}
                          onClick={() => handleDownloadKit(doc)}
                          className="group text-left flex items-start gap-3 p-3 bg-white rounded-lg border border-[#d4a850]/30 hover:border-[#d4a850] hover:shadow-md transition-all"
                          data-testid={`vip-kit-doc-${doc.id}`}
                        >
                          <div className="flex-shrink-0 w-10 h-10 rounded bg-[#fdfcf6] border border-[#d4a850]/40 flex items-center justify-center group-hover:bg-[#d4a850]/10">
                            <FileText size={18} className="text-[#b78a1e]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-[#0d1a12] truncate">{doc.label}</div>
                            {doc.description && (
                              <div className="text-[11px] text-neutral-500 truncate">{doc.description}</div>
                            )}
                            <div className="text-[10px] text-[#8a6d1e] mt-0.5 flex items-center gap-1">
                              <Download size={9} /> Télécharger
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer sticky avec safe-area */}
          <div
            className="px-4 sm:px-6 md:px-8 py-3 sm:py-4 bg-white border-t border-[#d4a850]/30 flex items-center justify-between gap-2 flex-shrink-0"
            style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
          >
            <div className="text-[10px] sm:text-[11px] text-[#8a7a56] italic truncate">
              Golf du Kempferhof · Plobsheim
            </div>
            <button
              onClick={onClose}
              className="px-4 sm:px-5 py-2 rounded bg-[#0d1a12] text-white text-sm hover:bg-[#1e3a2c] shadow-sm flex-shrink-0"
              data-testid="vip-continue-btn"
            >
              Continuer
            </button>
          </div>
        </div>
      </div>

      {/* Animations locales — pas de bundle CSS supplémentaire */}
      <style>{`
        @keyframes vipFadeIn {
          from { opacity: 0; transform: scale(0.92) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .vip-fade-in {
          animation: vipFadeIn 0.65s cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes vipCrestBloom {
          0% { transform: scale(0) rotate(-90deg); opacity: 0; box-shadow: 0 0 0 0 rgba(212, 168, 80, 0.7); }
          60% { transform: scale(1.15) rotate(15deg); opacity: 1; box-shadow: 0 0 50px 15px rgba(212, 168, 80, 0.35); }
          100% { transform: scale(1) rotate(0); opacity: 1; box-shadow: 0 0 30px 8px rgba(212, 168, 80, 0.2); }
        }
        .vip-crest {
          animation: vipCrestBloom 1.2s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        @keyframes vipPulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.3; }
          50% { transform: translate(-50%, -50%) scale(1.15); opacity: 0.5; }
        }
        .vip-pulse {
          animation: vipPulse 4s ease-in-out infinite;
        }
        .bg-gradient-radial {
          background-image: radial-gradient(circle, var(--tw-gradient-stops));
        }
      `}</style>
    </>
  );
}
